import React, { useState } from "react";

import { createSelector } from "@reduxjs/toolkit";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, Link } from "react-router-dom";
import { reduxForm, SubmissionError, formValueSelector, change } from "redux-form";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import { validateRequired, validateSigned } from "Utils/Validations";

import { getBorApplication, useSaveSignatureMutation } from "../api";
import BORPage from "../components/BORPage";
import EditBrokerOfRecordLetterModal from "../components/EditBrokerOfRecordLetterModal";
import { baseURL, currentPlanURL, contributionsURL } from "../urls";
import { H4, Title, Subtitle } from "Cobalt/Header";
import { useModal } from "Cobalt/Modal";
import BodyText from "Common/BodyText";
import Button from "Common/Button";
import { SIFormField, SIFormCheckbox } from "Common/forms";
import { Row, Col } from "Common/Grid";
import Icon from "Common/Icon";
import InfoPopover from "Common/InfoPopover";
import SignatureForm from "Common/Signature";

import makeBem from "Utils/makeBem";

const bem = makeBem("BORMainContent");

const CONFIRMATION_FORM = "confirmation";

const withReduxForm = reduxForm({
  form: CONFIRMATION_FORM,
  destroyOnUnmount: false,
  enableReinitialize: true,
  onSubmit: async (values, _dispatch, props) => {
    try {
      await props.saveSignature(values).unwrap();
    } catch (e) {
      const {
        data: { error, fieldErrors },
      } = e;
      throw new SubmissionError({
        _error: error,
        ...fieldErrors,
      });
    }
  },
  onSubmitSuccess: (_values, _dispatch, { history }) => {
    history.push(`${baseURL}${contributionsURL}`);
  },
});

const InlineBenefits = () => {
  const benefits = [
    { text: "No additional cost or fees", colProps: { lg: 4, md: 6 } },
    {
      text: "Automatic employee enrollments & terminations",
      colProps: { lg: 6, md: 6 },
    },

    { text: "Digital ID cards", colProps: { lg: 2, md: 6 } },
    { text: "Deductions synced to your payroll", colProps: { lg: 4, md: 6 } },
    {
      text: "Access to SimplyInsured’s team of experts and support",
      colProps: { lg: 6, md: 6 },
    },

    { text: "One click renewals", colProps: { lg: 2, md: 6 } },
  ];

  return (
    <div className={bem("inlineBenefits")}>
      {benefits.map(({ text, colProps }, index) => (
        <Col className={bem("inlineBenefits", "benefit")} {...colProps} key={`benefits.${index}`}>
          <span className={bem("inlineBenefits", "icon")}>
            <Icon icon="check" size="xs" flavor="regular" />
          </span>
          <BodyText className={bem("inlineBenefits", "text")} size="ns-sixteen">
            {text}
          </BodyText>
        </Col>
      ))}
    </div>
  );
};

export const Changes = () => {
  const notAffected = ["Your pricing", "Your payment method", "Your insurance policy", "Your group number", "Your renewal date", "Your doctors"];

  return (
    <Row style={{ marginTop: "30px" }}>
      <Col sm={4}>
        <Subtitle className={bem("changes", "title")}>What will change</Subtitle>
        <div className={bem("changes", "container")}>
          <BodyText className={bem("changes", "changeText")} size="medium">
            SimplyInsured will become your Broker of Record{" "}
            <InfoPopover
              className={bem("changes", "infoPopover")}
              id="borConfirmationPopover"
              triggerText="More details"
              infoText={`
              As your broker, SimplyInsured will be responsible for managing your
              company's relationship with the carrier; including adding and
              removing employees to and from your group, handling your
              plan changes and renewals, and advocating on your behalf for
              any carrier interactions
            `}
              displayComponent={
                <Button color="link" pl={0} className={bem("changes", "button")}>
                  More details
                </Button>
              }
            />
          </BodyText>
        </div>
      </Col>
      <Col sm={8}>
        <Subtitle className={bem("changes", "title")}>What will NOT change</Subtitle>
        <div className={bem("changes", "container")}>
          {notAffected.map((change, index) => (
            <BodyText className={bem("changes", "changeText")} key={`changes.${index}`} size="ns-sixteen">
              {change}
            </BodyText>
          ))}
        </div>
      </Col>
    </Row>
  );
};

const validateAgreed = (value) => {
  if (value === undefined || value === null || value === false || value.toString().trim() === "") {
    return "Please read and acknowledge the notification before moving forward";
  }
  return undefined;
};

const formSelector = formValueSelector(CONFIRMATION_FORM);

const SignForm = withReduxForm(({ handleSubmit, submitting }) => {
  const { isShowing, show, hide } = useModal();
  const [seenModalOnce, setSeenModalOnce] = useState(false);

  const hasAgreed = useSelector((state) => formSelector(state, "agreed"));
  const dispatch = useDispatch();
  const setAgreed = (value) => {
    if (seenModalOnce) {
      dispatch(change(CONFIRMATION_FORM, "agreed", value));
    } else {
      show();
      setSeenModalOnce(true);
    }
  };
  return (
    <>
      <Row className={bem("form", "titleSection")}>
        <Col sm={11}>
          <Title className={bem("form", "title")} pb={3}>
            Notify the carrier
          </Title>
          <BodyText className={bem("form", "text")} size="ns-sixteen">
            In order to notify your insurance carrier of this broker of record change, we will need to send the following{" "}
            <Button
              px={0}
              style={{ fontSize: "inherit" }}
              color="link"
              onClick={() => {
                setSeenModalOnce(true);
                show();
                AnalyticsTracking.trackEvent("cta_clicked", {
                  label: "bor_letter_modal_opened",
                });
              }}
            >
              letter
            </Button>
            . Please sign below to authorize this change.
          </BodyText>
          <EditBrokerOfRecordLetterModal show={isShowing} onHide={hide} onAgree={() => setAgreed(true)} />
        </Col>
      </Row>
      <form className={bem("form")} onSubmit={handleSubmit}>
        <Row>
          <SIFormField
            name="agreed"
            component={SIFormCheckbox}
            validate={validateAgreed}
            props={{
              onClick: () => {
                setAgreed(!hasAgreed);
                AnalyticsTracking.trackEvent("cta_clicked", {
                  label: "bor_letter_checkbox_clicked",
                });
              },
              label: (
                <>
                  I agree to{" "}
                  <Button
                    color="link"
                    p={0}
                    onClick={(e) => {
                      setSeenModalOnce(true);
                      e.stopPropagation();
                      show();
                      AnalyticsTracking.trackEvent("cta_clicked", {
                        label: "bor_letter_modal_opened",
                      });
                    }}
                  >
                    notify the carrier of the broker of record change
                  </Button>
                </>
              ),
            }}
          />
        </Row>
        <Row>
          <SIFormField
            name="signatureImageDataUrl"
            label="Your signature"
            component={SignatureForm}
            props={{
              showSubmitButton: false,
              clearButtonText: "Clear Signature",
            }}
            validate={(validateRequired, validateSigned)}
            mb={0}
            disabled={hasAgreed ? "disable" : undefined}
          />
        </Row>
        <Row>
          <Col smOffset={8} mdOffset={9}>
            <Button fullWidth submitting={submitting} disabled={submitting} size="medium" type="submit">
              Next
            </Button>
          </Col>
        </Row>
      </form>
    </>
  );
});

const formInitialValues = createSelector(getBorApplication, (borApplication) => {
  const { signatureImageDataUrl } = borApplication || {};
  return {
    signatureImageDataUrl,
  };
});

const ConfirmationPage = () => {
  const [saveSignature] = useSaveSignatureMutation();
  const history = useHistory();
  const initialValues = useSelector(formInitialValues);

  return (
    <BORPage>
      <Button component={Link} size="medium" to={`${baseURL}${currentPlanURL}`} color="link" className={bem("backButton")} pl={0}>
        <span>
          <Icon icon="chevron-left" pr={3} />
          Back
        </span>
      </Button>
      <H4 className={bem("title")}>Review and Sign</H4>
      <Subtitle className={bem("subtitle")}>Make SimplyInsured your broker of record and enjoy the following benefits:</Subtitle>
      <InlineBenefits />
      <Changes />
      <hr className={bem("divider")} />
      <SignForm saveSignature={saveSignature} history={history} initialValues={initialValues} />
    </BORPage>
  );
};

export default ConfirmationPage;
